import { useEffect, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PLAN_LIMITS } from "../lib/plans";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Plus,
  Trash2,
  BookOpen,
  Sparkles,
  Crown,
  Award,
  Lock,
} from "lucide-react";
import { toast } from "sonner";

const EMOJIS = ["🦸", "🦸‍♀️", "🧙", "🧚", "🦄", "🐉", "🚀", "🐱", "🐶", "🦊"];

// Kid dashboard: simplified view scoped to the logged-in kid
function KidDashboard({ user }) {
  const navigate = useNavigate();
  const [comics, setComics] = useState([]);

  useEffect(() => {
    api
      .get("/comics")
      .then((r) => setComics(r.data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-zinc-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1
              className="font-headings uppercase text-4xl md:text-5xl"
              data-testid="dashboard-title"
            >
              Hey {user?.avatar_emoji} {user?.nickname || user?.name}!
            </h1>
            <p className="font-body font-bold text-black/70 mt-1">
              Your comics are ready for adventure.
            </p>
          </div>
          <Button
            onClick={() => navigate(`/create?child=${user.child_id}`)}
            className="bg-pink-500 hover:bg-pink-400 text-white cv-border cv-shadow cv-press font-headings text-xl uppercase rounded-xl py-6 px-8"
            data-testid="kid-new-comic-btn"
          >
            <Sparkles size={20} className="mr-2" /> New Comic
          </Button>
        </div>

        <section>
          <h2 className="font-headings uppercase text-2xl mb-4">My Comics</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {comics.map((c) => (
              <Link
                to={`/comic/${c.id}`}
                key={c.id}
                data-testid={`comic-card-${c.id}`}
              >
                <div className="cv-border cv-shadow rounded-2xl bg-white overflow-hidden cv-press">
                  <div className="aspect-square halftone-purple flex items-center justify-center border-b-4 border-black relative overflow-hidden">
                    {c.panels?.[0]?.image_url ? (
                      <img
                        src={c.panels[0].image_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <BookOpen size={48} className="text-purple-700" />
                    )}
                    <span className="absolute top-2 right-2 bg-black text-yellow-300 px-2 py-0.5 font-headings text-xs uppercase rounded">
                      {c.status}
                    </span>
                  </div>
                  <div className="p-3">
                    <h3 className="font-headings uppercase text-lg leading-tight truncate">
                      {c.title}
                    </h3>
                    <p className="font-body font-bold text-xs text-black/60">
                      {c.page_count} pages · {c.art_style}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
            {comics.length === 0 && (
              <div className="md:col-span-4 cv-border cv-shadow rounded-2xl bg-pink-100 p-8 text-center font-body font-bold">
                No comics yet — tap "New Comic" to start your adventure! ✨
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [comics, setComics] = useState([]);
  const [badges, setBadges] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    nickname: "",
    age: 8,
    reading_level: "early",
    pin: "",
    avatar_emoji: "🦸",
  });
  const [busy, setBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  const load = async () => {
    const [p, c, b] = await Promise.all([
      api.get("/profiles"),
      api.get("/comics"),
      api.get("/badges/me"),
    ]);
    setProfiles(p.data);
    setComics(c.data);
    setBadges(b.data);
  };
  useEffect(() => {
    load();
    refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (user && user.role === "teacher") {
    return <Navigate to="/teacher" replace />;
  }

  // Kid users get a simplified dashboard
  if (user && user.role === "kid") {
    return <KidDashboard user={user} />;
  }

  const addProfile = async (e) => {
    e.preventDefault();
    if (form.pin.length < 4) {
      toast.error("PIN must be 4+ digits");
      return;
    }
    setBusy(true);
    try {
      await api.post("/profiles", { ...form, age: parseInt(form.age) });
      toast.success(`${form.nickname} added!`);
      setOpen(false);
      setForm({
        nickname: "",
        age: 8,
        reading_level: "early",
        pin: "",
        avatar_emoji: "🦸",
      });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this child profile?")) return;
    await api.delete(`/profiles/${id}`);
    load();
  };

  const planMeta = {
    free: { name: "Free Plan", color: "bg-zinc-200" },
    fun: { name: "Fun Pack", color: "bg-yellow-300" },
    excitement: { name: "Excitement", color: "bg-pink-300" },
    epic: { name: "Epic Pack", color: "bg-purple-300" },
  }[user?.plan || "free"];

  const planKey = user?.plan || "free";
  const booksLimit = PLAN_LIMITS[planKey] ?? 0;
  const booksUsed = user?.books_used_this_month ?? 0;
  const bonusBooks = user?.bonus_books ?? 0;
  const totalAllowed = booksLimit + bonusBooks;
  const atLimit = booksUsed >= totalAllowed;

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const r = await api.post("/payments/portal", { return_url: window.location.href });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open billing portal");
      setPortalBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1
              className="font-headings uppercase text-4xl md:text-5xl"
              data-testid="dashboard-title"
            >
              Hey {user?.name}!
            </h1>
            <p className="font-body font-bold text-black/70 mt-1">
              Manage your kids and their comics.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`cv-border ${planMeta.color} px-4 py-2 font-headings uppercase rounded-xl flex items-center gap-2`}
              data-testid="dashboard-plan"
            >
              <Crown size={18} /> {planMeta.name}
              {user?.cancel_at_period_end && (
                <span className="text-xs normal-case font-body font-bold text-black/60">
                  (cancels {user?.current_period_end ? new Date(user.current_period_end).toLocaleDateString() : "soon"})
                </span>
              )}
            </span>
            {planKey !== "free" && (
              <Button
                onClick={openPortal}
                disabled={portalBusy}
                variant="outline"
                className="cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl"
                data-testid="dashboard-manage-subscription"
              >
                {portalBusy ? "Opening..." : "Manage subscription"}
              </Button>
            )}
            <Link to="/pricing" data-testid="dashboard-upgrade">
              <Button className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl">
                Upgrade
              </Button>
            </Link>
          </div>
        </div>

        {/* USAGE METER */}
        {planKey !== "free" && (
          <div
            className={`cv-border rounded-2xl p-4 mb-8 flex flex-wrap items-center gap-4 ${atLimit ? "bg-red-50 border-red-400" : "bg-white"}`}
            data-testid="usage-meter"
          >
            <div className="flex-1 min-w-[180px]">
              <div className="flex items-center justify-between mb-1">
                <span className="font-headings uppercase text-sm">
                  Books this month
                </span>
                <span
                  className={`font-headings text-sm ${atLimit ? "text-red-600" : "text-black/70"}`}
                  data-testid="usage-count"
                >
                  {booksUsed} / {totalAllowed}
                </span>
              </div>
              <div className="w-full bg-zinc-200 rounded-full h-3 cv-border-3 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${atLimit ? "bg-red-500" : "bg-purple-500"}`}
                  style={{
                    width: `${totalAllowed > 0 ? Math.min((booksUsed / totalAllowed) * 100, 100) : 100}%`,
                  }}
                  data-testid="usage-bar"
                />
              </div>
              {bonusBooks > 0 && (
                <p className="font-body text-xs mt-1 text-black/60">
                  Includes {bonusBooks} bonus 🎁
                </p>
              )}
            </div>
            {atLimit ? (
              <Link to="/pricing">
                <Button
                  className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl shrink-0"
                  data-testid="usage-upgrade-btn"
                >
                  <Crown size={16} className="mr-1" /> Upgrade for more
                </Button>
              </Link>
            ) : (
              <span className="font-body font-bold text-sm text-black/60 shrink-0">
                {totalAllowed - booksUsed} book
                {totalAllowed - booksUsed !== 1 ? "s" : ""} remaining
              </span>
            )}
          </div>
        )}

        {/* FREE PLAN BANNER */}
        {planKey === "free" && (
          <div
            className="cv-border rounded-2xl bg-yellow-50 border-yellow-400 p-4 mb-8 flex flex-wrap items-center justify-between gap-4"
            data-testid="free-plan-banner"
          >
            <div>
              <p className="font-headings uppercase text-base">
                You're on the Free Plan
              </p>
              <p className="font-body font-bold text-sm text-black/70">
                Subscribe to start generating comics for your kids.
              </p>
            </div>
            <Link to="/pricing">
              <Button
                className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl"
                data-testid="free-upgrade-btn"
              >
                <Crown size={16} className="mr-1" /> Pick a plan
              </Button>
            </Link>
          </div>
        )}

        {/* CHILDREN */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-headings uppercase text-2xl">
              Your Kids ({profiles.length}/3)
            </h2>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  disabled={profiles.length >= 3}
                  className="bg-yellow-400 hover:bg-yellow-300 text-black cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl"
                  data-testid="add-child-btn"
                >
                  <Plus size={18} className="mr-1" /> Add child
                </Button>
              </DialogTrigger>
              <DialogContent className="cv-border cv-shadow-lg rounded-2xl bg-white">
                <DialogHeader>
                  <DialogTitle className="font-headings uppercase text-2xl">
                    Add a child
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={addProfile} className="space-y-3">
                  <div>
                    <Label className="font-headings uppercase">Nickname</Label>
                    <Input
                      required
                      className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                      pattern="[a-zA-Z0-9_\-]+"
                      title="Letters, numbers, underscores and dashes only"
                      placeholder="e.g. superheroBob"
                      value={form.nickname}
                      onChange={(e) =>
                        setForm({ ...form, nickname: e.target.value })
                      }
                      data-testid="child-name"
                    />
                    <p className="text-xs font-body mt-1 text-black/60">
                      Letters, numbers, underscores (_) and dashes (-) only.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="font-headings uppercase">
                        Age (5+)
                      </Label>
                      <Input
                        type="number"
                        min={5}
                        required
                        className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                        value={form.age}
                        onChange={(e) =>
                          setForm({ ...form, age: e.target.value })
                        }
                        data-testid="child-age"
                      />
                    </div>
                    <div>
                      <Label className="font-headings uppercase">Reading</Label>
                      <Select
                        value={form.reading_level}
                        onValueChange={(v) =>
                          setForm({ ...form, reading_level: v })
                        }
                      >
                        <SelectTrigger
                          className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                          data-testid="child-reading"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="early">Early reader</SelectItem>
                          <SelectItem value="fluent">Fluent reader</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="font-headings uppercase">
                      Child PIN (4+ digits)
                    </Label>
                    <Input
                      required
                      minLength={4}
                      pattern="[0-9]+"
                      type="password"
                      className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                      value={form.pin}
                      onChange={(e) =>
                        setForm({ ...form, pin: e.target.value })
                      }
                      data-testid="child-pin"
                    />
                  </div>
                  <div>
                    <Label className="font-headings uppercase mb-2 block">
                      Avatar emoji
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {EMOJIS.map((e) => (
                        <button
                          type="button"
                          key={e}
                          onClick={() => setForm({ ...form, avatar_emoji: e })}
                          className={`w-12 h-12 cv-border-3 rounded-xl text-2xl flex items-center justify-center ${
                            form.avatar_emoji === e
                              ? "bg-yellow-300 cv-shadow-sm"
                              : "bg-white"
                          }`}
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={busy}
                      className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl"
                      data-testid="child-submit"
                    >
                      {busy ? "Adding..." : "Add child"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {profiles.map((p) => (
              <div
                key={p.id}
                className="cv-border cv-shadow rounded-2xl bg-white p-5 cv-press relative"
                data-testid={`child-card-${p.id}`}
              >
                <div className="text-5xl mb-2">{p.avatar_emoji}</div>
                <h3 className="font-headings uppercase text-xl">
                  {p.nickname}
                </h3>
                <p className="font-body font-bold text-sm text-black/70">
                  Age {p.age} · {p.reading_level} reader
                </p>
                <div className="mt-4 flex gap-2">
                  <Button
                    onClick={() => {
                      if (atLimit) {
                        toast.error(
                          planKey === "free"
                            ? "Subscribe to a plan to create comics."
                            : "Monthly limit reached — upgrade your plan for more comics.",
                          {
                            action: {
                              label: "Upgrade",
                              onClick: () => navigate("/pricing"),
                            },
                          },
                        );
                        return;
                      }
                      navigate(`/create?child=${p.id}`);
                    }}
                    className={`cv-border-3 cv-shadow-sm cv-press font-headings uppercase text-sm rounded-lg ${
                      atLimit
                        ? "bg-zinc-200 text-zinc-500 cursor-not-allowed"
                        : "bg-pink-500 hover:bg-pink-400 text-white"
                    }`}
                    data-testid={`make-comic-${p.id}`}
                  >
                    {atLimit ? (
                      <>
                        <Lock size={16} className="mr-1" /> Limit reached
                      </>
                    ) : (
                      <>
                        <Sparkles size={16} className="mr-1" /> New comic
                      </>
                    )}
                  </Button>
                  <button
                    onClick={() => remove(p.id)}
                    className="cv-border-3 bg-white p-2 rounded-lg cv-shadow-sm cv-press"
                    data-testid={`delete-child-${p.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
            {profiles.length === 0 && (
              <div className="md:col-span-3 cv-border cv-shadow rounded-2xl bg-yellow-100 p-8 text-center font-body font-bold">
                No kids yet — add your first child to start making comics! 🦸
              </div>
            )}
          </div>
        </section>

        {/* COMICS */}
        <section className="mb-12">
          <h2 className="font-headings uppercase text-2xl mb-4">
            Comic Library
          </h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {comics.map((c) => (
              <Link
                to={`/comic/${c.id}`}
                key={c.id}
                data-testid={`comic-card-${c.id}`}
              >
                <div className="cv-border cv-shadow rounded-2xl bg-white overflow-hidden cv-press">
                  <div className="aspect-square halftone-purple flex items-center justify-center border-b-4 border-black relative overflow-hidden">
                    {c.panels?.[0]?.image_url ? (
                      <img
                        src={c.panels[0].image_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <BookOpen size={48} className="text-purple-700" />
                    )}
                    <span className="absolute top-2 right-2 bg-black text-yellow-300 px-2 py-0.5 font-headings text-xs uppercase rounded">
                      {c.status}
                    </span>
                  </div>
                  <div className="p-3">
                    <h3 className="font-headings uppercase text-lg leading-tight truncate">
                      {c.title}
                    </h3>
                    <p className="font-body font-bold text-xs text-black/60">
                      {c.page_count} pages · {c.art_style}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
            {comics.length === 0 && (
              <div className="md:col-span-4 cv-border cv-shadow rounded-2xl bg-pink-100 p-8 text-center font-body font-bold">
                No comics yet — pick a kid and tap "New comic"! ✨
              </div>
            )}
          </div>
        </section>

        {/* BADGES */}
        <section className="mb-12">
          <h2 className="font-headings uppercase text-2xl mb-4 flex items-center gap-2">
            <Award size={26} /> Achievements
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {badges.map((b) => (
              <div
                key={b.key}
                className={`cv-border-3 rounded-2xl p-4 text-center ${
                  b.earned
                    ? "bg-yellow-300 cv-shadow-sm"
                    : "bg-zinc-100 opacity-60"
                }`}
                data-testid={`badge-${b.key}`}
              >
                <div
                  className="text-4xl mb-1"
                  style={{ filter: b.earned ? "none" : "grayscale(1)" }}
                >
                  {b.emoji}
                </div>
                <div className="font-headings uppercase text-sm leading-tight">
                  {b.name}
                </div>
                <div className="font-body text-xs mt-1 text-black/70">
                  {b.desc}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
